/**
 * Состояние стакана по одному токену и процедура его восстановления.
 *
 * Здесь только чистые функции — их и покрывают тесты. Всё, что связано с сетью,
 * таймерами и React, живёт в useBooks.ts.
 *
 * Процедура из docs/bus-contracts.md, дословно:
 *   1. подписались → БУФЕРИЗУЕМ, ничего не применяя;
 *   2. прочитали GET /api/v1/book/{asset_id} → seq = S;
 *   3. выбросили из буфера всё с seq <= S, остальное применили по возрастанию;
 *   4. дельта с seq != last+1 — пропуск: перечитать снапшот.
 *
 * Три вещи, которых в контракте нет, но без которых лесенка врёт:
 *
 *  - Снапшот СТАРШЕ применённого состояния не принимается. Ключ book:<asset_id>
 *    переписывается трейдером раз в ~10 с (MD_SNAPSHOT_TTL/3) и отстаёт от
 *    канала, так что принять его значило бы откатить стакан назад.
 *  - Разрыв seq при открытии рынка — НОРМА, а не поломка. Дельты, вышедшие
 *    между последней записью ключа и моментом нашей подписки, потеряны
 *    безвозвратно. Размер уровня абсолютный, поэтому применять дельты поверх
 *    слегка отставшего снапшота безопасно: каждый затронутый уровень становится
 *    точным. Неверными остаются лишь уровни, изменённые внутри потерянного окна
 *    и больше не тронутые. Поэтому лесенка рисуется, но честно помечена
 *    «синхронизация», пока цепочка не сомкнётся.
 *  - Рестарт трейдера начинает счёт seq заново, то есть присылает НОМЕРА НИЖЕ
 *    наших. Без отдельной ветки такие дельты отбрасывались бы как «отставшие»,
 *    и лесенка молча замерла бы навсегда — худший исход для этого экрана.
 */
import type { BookLevel, BookLevelChange, BookUpdate } from "../api/types";
import type { Micro } from "../lib/money";
import * as money from "../lib/money";

/** Уровень с уже разобранной ценой: строка с провода парсится один раз при
 *  приёме, а не на каждом шаге поиска и не на каждом кадре отрисовки. */
export interface Level {
  price: string;
  size: string;
  p: Micro;
  s: Micro;
}

export type BookStatus =
  | "waiting" // подписаны, опорного снапшота ещё нет — рисовать нечего
  | "syncing" // стакан есть и рисуется, но цепочка seq не сомкнута
  | "live" // снапшот и непрерывная цепочка seq
  | "absent" // 404: ключа нет — трейдер молчит или стакан у него не подтверждён
  | "error";

export interface AssetBook {
  assetId: string;
  conditionId: string;
  bids: Level[]; // по убыванию цены
  asks: Level[]; // по возрастанию
  tickSize: string;
  lastTradePrice: string | null;
  /** Флаг из снапшота: трейдер сам считает стакан неподтверждённым. */
  snapshotStale: boolean;
  /** ЧАСЫ ТРЕЙДЕРА (Book.received_at). Только чтобы показать время снапшота. */
  snapshotAt: number;
  /** ЧАСЫ БРАУЗЕРА. Только для «нет обновлений N с».
   *  Вычитать одно из другого НЕЛЬЗЯ: часы контейнера и браузера расходятся,
   *  и такая разность была бы уверенной ложью. */
  appliedAt: number;
  seq: number;
  /** Сколько обновлений заведомо пропущено — показывается оператору. */
  missed: number;
  status: BookStatus;
  error: string | null;
  /** Дельты, пришедшие до опорного снапшота (шаг 1 процедуры). */
  buffer: BookUpdate[];
}

/** Потолок буфера дельт до снапшота. */
export const BUFFER_LIMIT = 500;

/**
 * Насколько ниже нашего seq должна быть дельта, чтобы счесть её рестартом
 * трейдера, а не отставшим сообщением. Канал теряет сообщения, но не
 * переупорядочивает их на сотни позиций.
 */
export const SEQ_RESET_GUARD = 100;

type Dir = "desc" | "asc";

export function emptyBook(assetId: string, conditionId: string): AssetBook {
  return {
    assetId,
    conditionId,
    bids: [],
    asks: [],
    tickSize: "",
    lastTradePrice: null,
    snapshotStale: false,
    snapshotAt: 0,
    appliedAt: 0,
    seq: 0,
    missed: 0,
    status: "waiting",
    error: null,
    buffer: [],
  };
}

export function toLevels(src: BookLevel[] | undefined, dir: Dir): Level[] {
  const out: Level[] = [];
  for (const lvl of src ?? []) {
    const p = money.parse(lvl.price);
    const s = money.parse(lvl.size);
    // Битый уровень пропускаем поштучно: одно нечитаемое значение не должно
    // стоить оператору всего стакана.
    if (p === null || s === null || s === 0) continue;
    out.push({ price: lvl.price, size: lvl.size, p, s });
  }
  // Пересортировка, хотя контракт domain.Book порядок и гарантирует: на вехе M2
  // провод уже один раз противоречил документации (bids шли по возрастанию),
  // а сортировка двух сотен уровней на снапшот бесплатна.
  out.sort((a, b) => (dir === "desc" ? b.p - a.p : a.p - b.p));
  return out;
}

/** Позиция уровня с ценой p, либо -1. */
function indexOf(levels: readonly Level[], p: Micro, dir: Dir): number {
  const i = insertionPoint(levels, p, dir);
  const cur = levels[i];
  return cur !== undefined && cur.p === p ? i : -1;
}

function insertionPoint(levels: readonly Level[], p: Micro, dir: Dir): number {
  let lo = 0;
  let hi = levels.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const cur = levels[mid];
    // Индекс заведомо в границах, но noUncheckedIndexedAccess этого не знает.
    if (cur === undefined) break;
    if (dir === "desc" ? cur.p > p : cur.p < p) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Применение изменений к одной стороне стакана.
 *
 * Два правила контракта, на которых легко ошибиться:
 *   size "0" — УДАЛЕНИЕ уровня, а не нулевой объём;
 *   размер АБСОЛЮТНЫЙ — это новое состояние уровня, а не приращение к прежнему.
 * Из второго следует идемпотентность: повторно применённая дельта безвредна.
 */
export function applyChanges(src: Level[], changes: BookLevelChange[], dir: Dir): Level[] {
  if (changes.length === 0) return src;
  // Копия одна на обновление, а не на изменение: массив уровней — это
  // идентичность для React, менять его на месте нельзя.
  const out = src.slice();
  for (const c of changes) {
    const p = money.parse(c.price);
    const s = money.parse(c.size);
    if (p === null || s === null) continue;

    const i = indexOf(out, p, dir);
    if (s === 0) {
      if (i >= 0) out.splice(i, 1);
      continue;
    }
    const lvl: Level = { price: c.price, size: c.size, p, s };
    if (i >= 0) out[i] = lvl;
    else out.splice(insertionPoint(out, p, dir), 0, lvl);
  }
  return out;
}

function sideChanges(changes: BookLevelChange[] | undefined, side: "BUY" | "SELL"): BookLevelChange[] {
  return (changes ?? []).filter((c) => c.side === side);
}

function buffered(prev: AssetBook, u: BookUpdate): AssetBook {
  const buffer = [...prev.buffer, u];
  // Буфер ограничен: на активном рынке ожидание снапшота при недоступном
  // web_api иначе съело бы память вкладки.
  if (buffer.length > BUFFER_LIMIT) buffer.splice(0, buffer.length - BUFFER_LIMIT);
  return { ...prev, buffer };
}

/** Применение дельты. Возвращает прежнее состояние, если применять нечего. */
export function applyDelta(prev: AssetBook, u: BookUpdate, now = Date.now()): AssetBook {
  // Счётчик у трейдера начался заново — это не отставшая дельта, а рестарт.
  // Без этой ветки все дельты после рестарта отбрасывались бы как «старые»,
  // и лесенка молча замерла бы на прошлом состоянии.
  if (prev.seq - u.seq > SEQ_RESET_GUARD) {
    return { ...emptyBook(prev.assetId, prev.conditionId), buffer: [u] };
  }

  // Опоры нет — только копим (шаг 1 процедуры).
  if (prev.status === "waiting" || prev.status === "absent" || prev.status === "error") {
    return buffered(prev, u);
  }
  if (u.seq <= prev.seq) return prev; // повтор или отставшая дельта

  const gap = u.seq !== prev.seq + 1;
  const next: AssetBook = {
    ...prev,
    bids: applyChanges(prev.bids, sideChanges(u.changes, "BUY"), "desc"),
    asks: applyChanges(prev.asks, sideChanges(u.changes, "SELL"), "asc"),
    seq: u.seq,
    appliedAt: now,
  };
  if (!gap) return next;

  return { ...next, status: "syncing", missed: prev.missed + (u.seq - prev.seq - 1) };
}

/**
 * Принятие снапшота — шаги 2 и 3 процедуры.
 *
 * Источник у снапшота два и разбираются они одним кодом: ключ book:<asset_id>
 * через REST и сообщение book_snapshot из канала. Ради этого свойства web_api и
 * отдаёт по REST тот же конверт, что летит в сокет.
 */
export function fromSnapshot(prev: AssetBook, u: BookUpdate, now = Date.now()): AssetBook {
  const book = u.book;
  if (!book) return { ...prev, status: "error", error: "снапшот без стакана" };

  // Счётчик у трейдера начался заново — это рестарт, а не отставший ключ
  // (та же ветка, что в applyDelta). Прежнее состояние и буфер принадлежат
  // старой эпохе seq: реплей их поверх новой опоры унёс бы seq на прежнюю
  // высоту, и все настоящие дельты новой эпохи отбрасывались бы как «старые».
  if (prev.status !== "waiting" && prev.status !== "absent" && prev.seq - u.seq > SEQ_RESET_GUARD) {
    prev = emptyBook(prev.assetId, prev.conditionId);
  }

  // Снапшот старше применённого состояния — откат назад, а не восстановление.
  if (prev.status !== "waiting" && prev.status !== "absent" && u.seq < prev.seq) {
    return prev;
  }

  let next: AssetBook = {
    ...prev,
    bids: toLevels(book.bids, "desc"),
    asks: toLevels(book.asks, "asc"),
    tickSize: book.tick_size,
    lastTradePrice: book.last_trade_price ?? null,
    snapshotStale: book.stale,
    snapshotAt: Date.parse(book.received_at),
    appliedAt: now,
    seq: u.seq,
    missed: 0,
    status: "live",
    error: null,
    buffer: [],
  };

  // Шаг 3: seq <= S выбрасываем, остальное применяем по возрастанию.
  const replay = prev.buffer.filter((d) => d.seq > u.seq).sort((a, b) => a.seq - b.seq);
  for (const d of replay) next = applyDelta(next, d, now);
  return next;
}

/** Накопленный объём по уровням, от вершины стакана наружу. */
export function cumulative(levels: readonly Level[], n: number): Micro[] {
  const out: Micro[] = [];
  let acc = 0;
  for (let i = 0; i < Math.min(n, levels.length); i++) {
    const lvl = levels[i];
    if (lvl === undefined) break;
    acc = money.add(acc, lvl.s) ?? acc;
    out.push(acc);
  }
  return out;
}

/** Лучшая цена стороны, если она есть. */
export function best(levels: readonly Level[]): Level | null {
  return levels[0] ?? null;
}
